export const APP_NAME = "Sistema de Gerenciamento Empresarial";

export interface PageMeta {
  title: string;
  description: string;
}

const DEFAULT_META: PageMeta = {
  title: APP_NAME,
  description:
    "Gestão de empresas em um só lugar: equipe, ponto, clientes, projetos, documentos e finanças.",
};

const NOT_FOUND_META: PageMeta = {
  title: "Página não encontrada",
  description: "O endereço acessado não existe ou foi movido.",
};

const PAGE_META: Record<string, PageMeta> = {
  "/login": {
    title: "Entrar",
    description: "Acesse sua conta do Sistema de Gerenciamento Empresarial.",
  },
  "/cadastro": {
    title: "Criar conta",
    description: "Crie a conta da sua empresa e comece a organizar a gestão.",
  },
  "/redefinir-senha": {
    title: "Esqueci minha senha",
    description: "Receba por e-mail um link para redefinir sua senha.",
  },
  "/resetar-senha": {
    title: "Redefinir senha",
    description: "Defina uma nova senha para acessar sua conta.",
  },
  "/criar-senha": {
    title: "Criar senha",
    description: "Defina sua senha para ativar o acesso ao sistema.",
  },
  "/s/:token": {
    title: "Compartilhado com você",
    description: "Conteúdo compartilhado por link.",
  },
  "/terminal": {
    title: "Terminal de ponto",
    description: "Registro de ponto dos funcionários neste dispositivo.",
  },
  "/facial/:code": {
    title: "Ponto digital",
    description: "Registre seu ponto com reconhecimento facial.",
  },
  "/cadastro-facial/:token": {
    title: "Cadastro facial",
    description: "Cadastre seu rosto para registrar o ponto.",
  },

  // Geral
  "/visao-geral": {
    title: "Visão geral",
    description: "Resumo das empresas, pendências e indicadores da conta.",
  },
  "/perfil": {
    title: "Minha conta",
    description: "Seus dados pessoais, senha, acessos e atividade.",
  },
  "/notificacoes": {
    title: "Notificações",
    description: "Histórico de avisos de ponto, documentos e tarefas.",
  },
  "/usuarios": {
    title: "Usuários",
    description: "Quem acessa a conta e o que cada pessoa pode ver.",
  },
  "/usuarios/:id": {
    title: "Usuário",
    description: "Dados, empresas e permissões do usuário.",
  },
  "/plano": {
    title: "Plano",
    description: "Plano da conta, uso dos limites e troca de assinatura.",
  },
  "/empresas": {
    title: "Empresas",
    description: "Empresas da conta e seus dados cadastrais.",
  },
  "/empresas/criar": {
    title: "Nova empresa",
    description: "Cadastre uma nova empresa na conta.",
  },
  "/empresas/:id": {
    title: "Empresa",
    description: "Dados cadastrais e configurações da empresa.",
  },
  "/contas": {
    title: "Contas",
    description: "Contas clientes da plataforma e atendimento.",
  },
  "/contas/:id": {
    title: "Conta",
    description: "Detalhes, plano e usuários da conta.",
  },
  "/inicio": {
    title: "Início",
    description: "O que está acontecendo hoje na empresa.",
  },
  "/meu-dia": {
    title: "Meu dia",
    description: "Suas tarefas, compromissos e pendências de hoje.",
  },
  "/meu-ponto": {
    title: "Meu ponto",
    description: "Suas marcações, espelho de ponto e pedidos de ajuste.",
  },
  "/meu-ponto/comprovante/:id": {
    title: "Comprovante de ponto",
    description: "Comprovante de registro da marcação de ponto.",
  },
  "/departamentos": {
    title: "Departamentos",
    description: "Estrutura de departamentos da empresa.",
  },
  "/departamentos/criar": {
    title: "Novo departamento",
    description: "Cadastre um departamento da empresa.",
  },
  "/departamentos/:id": {
    title: "Departamento",
    description: "Dados e membros do departamento.",
  },
  "/funcionarios": {
    title: "Funcionários",
    description: "Equipe da empresa, contratos e documentos.",
  },
  "/funcionarios/criar": {
    title: "Novo funcionário",
    description: "Cadastre um funcionário da empresa.",
  },
  "/funcionarios/:id": {
    title: "Funcionário",
    description: "Ficha, contratos, jornada e ponto do funcionário.",
  },
  "/ponto": {
    title: "Marcações de ponto",
    description: "Marcações de ponto dos funcionários por dia.",
  },
  "/ponto/lancar": {
    title: "Lançar marcação",
    description: "Lance uma marcação de ponto manualmente.",
  },
  "/ponto/ajustes": {
    title: "Ajustes de ponto",
    description: "Pedidos de ajuste de ponto aguardando análise.",
  },
  "/ponto/justificativas": {
    title: "Justificativas de ausência",
    description: "Faltas, atrasos e saídas antecipadas aguardando análise.",
  },
  "/ponto/espelhos": {
    title: "Espelhos de ponto",
    description: "Espelho mensal apurado de cada funcionário.",
  },
  "/ponto/espelhos/:employeeId": {
    title: "Espelho do funcionário",
    description: "Totais apurados e dias da competência do funcionário.",
  },
  "/ponto/:id": {
    title: "Marcação de ponto",
    description: "Detalhes e histórico da marcação de ponto.",
  },
  "/jornadas": {
    title: "Jornadas",
    description: "Jornadas de trabalho e escalas da empresa.",
  },
  "/jornadas/criar": {
    title: "Nova jornada",
    description: "Cadastre uma jornada de trabalho.",
  },
  "/jornadas/:id": {
    title: "Jornada",
    description: "Horários e regras da jornada de trabalho.",
  },
  "/feriados": {
    title: "Feriados",
    description: "Feriados nacionais, regionais e da empresa.",
  },
  "/feriados/criar": {
    title: "Novo feriado",
    description: "Cadastre um feriado ou recesso da empresa.",
  },
  "/feriados/:id": {
    title: "Feriado",
    description: "Dados do feriado.",
  },
  "/terminais": {
    title: "Terminais de ponto",
    description: "Dispositivos autorizados a registrar o ponto.",
  },
  "/terminal-publico": {
    title: "Terminal público",
    description: "Link público de ponto facial da empresa.",
  },
  "/leads": {
    title: "Leads",
    description: "Funil de vendas e oportunidades em andamento.",
  },
  "/clientes": {
    title: "Clientes",
    description: "Carteira de clientes da empresa.",
  },
  "/clientes/criar": {
    title: "Novo cliente",
    description: "Cadastre um cliente da empresa.",
  },
  "/clientes/:id": {
    title: "Cliente",
    description: "Dados, contatos, projetos e assinaturas do cliente.",
  },
  "/contatos": {
    title: "Contatos",
    description: "Pessoas de contato dos clientes.",
  },
  "/contatos/criar": {
    title: "Novo contato",
    description: "Cadastre uma pessoa de contato.",
  },
  "/contatos/:id": {
    title: "Contato",
    description: "Dados da pessoa de contato.",
  },
  "/projetos": {
    title: "Projetos",
    description: "Projetos da empresa e o andamento de cada um.",
  },
  "/projetos/criar": {
    title: "Novo projeto",
    description: "Cadastre um projeto.",
  },
  "/projetos/:id": {
    title: "Projeto",
    description: "Tarefas, horas e equipe do projeto.",
  },
  "/assinantes": {
    title: "Assinantes",
    description: "Assinaturas recorrentes dos clientes.",
  },
  "/financeiro": {
    title: "Financeiro",
    description: "Receitas, recebimentos e indicadores financeiros.",
  },
  "/faturas": {
    title: "Faturas",
    description: "Faturas emitidas e seus pagamentos.",
  },
  "/faturas/nova": {
    title: "Nova fatura",
    description: "Emita uma fatura para um cliente.",
  },
  "/faturas/:id": {
    title: "Fatura",
    description: "Itens, vencimento e pagamento da fatura.",
  },
  "/faturas/:id/imprimir": {
    title: "Imprimir fatura",
    description: "Fatura para impressão, PDF e imagem.",
  },
  "/faturas/:id/editar": {
    title: "Editar fatura",
    description: "Itens, valores e vencimento de uma fatura em rascunho.",
  },
  "/contratos-cobranca": {
    title: "Contratos de cobrança",
    description: "Condições de cobrança combinadas com cada cliente.",
  },
  "/contratos-cobranca/criar": {
    title: "Novo contrato de cobrança",
    description: "Cadastre as condições de cobrança de um cliente.",
  },
  "/contratos-cobranca/:id": {
    title: "Contrato de cobrança",
    description: "Valores, vencimento, vigência e status do contrato.",
  },
  "/documentos": {
    title: "Documentos",
    description: "Arquivos da empresa e documentos a vencer.",
  },
  "/kanban": {
    title: "Kanban",
    description: "Quadros de tarefas da equipe.",
  },
  "/kanban/:id": {
    title: "Quadro",
    description: "Colunas e cartões do quadro de tarefas.",
  },
  "/ferias": {
    title: "Férias",
    description: "Férias programadas e em andamento da equipe.",
  },
  "/ferias/criar": {
    title: "Novas férias",
    description: "Programe as férias de um funcionário.",
  },
  "/ferias/:id": {
    title: "Férias",
    description: "Período e situação das férias.",
  },
  "/configuracoes": {
    title: "Configurações",
    description:
      "Identidade, funil, módulos, lembretes e integrações da empresa.",
  },
  "/integrations/geniapay/callback": {
    title: "Conectando o GeniaPay",
    description: "Conclusão da conexão da empresa com o GeniaPay.",
  },
  "/integrations/geniapay/register": {
    title: "Registrando o SGE no GeniaPay",
    description: "Conclusão do cadastro do SGE como aplicativo no GeniaPay.",
  },
  "/plataforma/geniapay": {
    title: "GeniaPay",
    description: "Cadastro do SGE como aplicativo no GeniaPay.",
  },
  "/auditoria": {
    title: "Auditoria",
    description: "Histórico de quem criou, alterou ou excluiu cada registro.",
  },
};

const segments = (path: string) => path.split("/").filter(Boolean);

const matches = (pattern: string, pathname: string) => {
  const expected = segments(pattern);
  const actual = segments(pathname);
  return (
    expected.length === actual.length &&
    expected.every(
      (part, index) => part.startsWith(":") || part === actual[index]
    )
  );
};

const paramCount = (pattern: string) =>
  segments(pattern).filter((part) => part.startsWith(":")).length;

export const resolvePageMeta = (pathname: string): PageMeta => {
  if (segments(pathname).length === 0) return DEFAULT_META;

  const pattern = Object.keys(PAGE_META)
    .filter((candidate) => matches(candidate, pathname))
    .sort((a, b) => paramCount(a) - paramCount(b))[0];

  return pattern ? PAGE_META[pattern] : NOT_FOUND_META;
};

export const documentTitle = ({ title }: PageMeta) =>
  title === APP_NAME ? APP_NAME : `${title} | ${APP_NAME}`;
